import { useAppDispatch, useAppSelector } from "@/state";
import { ProjectsApi, ReleasesApi } from "@/state/api";
import {
  clearSelectedProject,
  clearSelectedProjectSheet,
  clearSelectedRelease,
  setSelectedProject,
  setSelectedProjectSheet,
  setSelectedRelease,
} from "@/state/slices";
import { Separator, Stack } from "@chakra-ui/react";
import { FormLabel } from "@components/form";
import { ComboBox, type ComboboxEventSelectPayload } from "@ui";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";


const ActionMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease
  );
  const selectedProjectSheet = useAppSelector(
    (state) => state.project.selectedProjectSheet
  );
  const { pathname } = useLocation();

  // RFIs/Submittals is project-only
  const isRfiSubmittals = pathname.startsWith("/rfisubmittals");


  const {
    isLoading: isProjectsLoading,
    isSuccess: isProjectsSuccess,
    data: allProjects,
  } = ProjectsApi.Api.useGetAllProjectsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const hasAuthoritativeProjects =
    isProjectsSuccess && Array.isArray(allProjects);
  const accessibleSelectedProject = useMemo(
    () =>
      hasAuthoritativeProjects &&
      selectedProject?.id &&
      allProjects?.some((project) => project.id === selectedProject.id)
        ? selectedProject
        : null,
    [allProjects, hasAuthoritativeProjects, selectedProject]
  );
  const accessibleSelectedRelease = accessibleSelectedProject
    ? selectedRelease
    : null;
  const accessibleSelectedProjectSheet = accessibleSelectedProject
    ? selectedProjectSheet
    : null;

  useEffect(() => {
    if (
      hasAuthoritativeProjects &&
      selectedProject?.id &&
      !accessibleSelectedProject
    ) {
      dispatch(clearSelectedProject());
      dispatch(clearSelectedRelease());
      dispatch(clearSelectedProjectSheet());
    }
  }, [
    accessibleSelectedProject,
    dispatch,
    hasAuthoritativeProjects,
    selectedProject?.id,
  ]);

    const { isLoading: isReleasesLoading, data: releases } =
    ReleasesApi.Api.useGetAllReleaseQuery(undefined, {
      skip: !accessibleSelectedProject?.id || isRfiSubmittals,
      refetchOnMountOrArgChange: true,
    });
  
  const { isLoading: isProjectSheetsLoading, data: projectSheets } =
    ProjectsApi.Api.useGetAllProjectSheetsQuery(undefined, {
      skip: !accessibleSelectedProject?.id,
      refetchOnMountOrArgChange: true,
    });

  const filteredReleases = useMemo(
    () =>
      releases?.filter(
        (release) => release.project === accessibleSelectedProject?.id
      ),
    [releases, accessibleSelectedProject]
  );
  const filteredProjectSheets = useMemo(
    () =>
      projectSheets?.filter(
        (projectSheet) =>
          projectSheet.project === accessibleSelectedProject?.id
      ),
    [projectSheets, accessibleSelectedProject]
  );

  const handleSelectProject = (value: ComboboxEventSelectPayload) => {
    dispatch(clearSelectedRelease());
    dispatch(clearSelectedProjectSheet());
    if (
      typeof value === "object" &&
      "value" in value &&
      "items" in value &&
      typeof (value as any).value === "number" &&
      Number.isFinite((value as any).value)
    ) {
      dispatch(
        setSelectedProject({
          id: value.value as number,
          item: value.items,
        })
      );
    } else {
      dispatch(clearSelectedProject());
      dispatch(clearSelectedRelease());
      dispatch(clearSelectedProjectSheet());
    }
  };

  const handleSelectRelease = (value: ComboboxEventSelectPayload) => {
    if (
      typeof value === "object" &&
      "value" in value &&
      "items" in value &&
      typeof (value as any).value === "number" &&
      Number.isFinite((value as any).value)
    ) {
      dispatch(
        setSelectedRelease({
          id: value.value as number,
          item: value.items,
        })
      );
    } else {
      dispatch(clearSelectedRelease());
    }
  };

  const handleSelectProjectSheet = (value: ComboboxEventSelectPayload) => {
    if (
      typeof value === "object" &&
      "value" in value &&
      "items" in value &&
      typeof (value as any).value === "number" &&
      Number.isFinite((value as any).value)
    ) {
      dispatch(
        setSelectedProjectSheet({
          id: value.value as number,
          item: value.items,
        })
      );
    } else {
      dispatch(clearSelectedProjectSheet());
    }
  };

  return (
    <Stack
      key={`actionMenu-${isProjectsLoading}-${isReleasesLoading}`}
      flexDirection={{ base: "column", md: "row" }}
      justifyContent={{ base: "center", md: "center" }}
      alignItems={{ base: "flex-start", md: "center" }}
      justifySelf={{ base: "center", md: "center" }}
      gap={{ base: 1, md: 10, lg: 20, xl: 30 }}
      m={{ base: 1, md: 2 }}
      minWidth={{ base: "100%", md: "90%", lg: "80%", xl: "70%" }}
    >
      <FormLabel
        label="Project"
        labelProps={{ minWidth: "fit-content", color: "accent1.contrast" }}
        infoTipColor="accent1.contrast"
      >
        <ComboBox
          data-testid="tutorial-project-selector"
          data-tutorial-id="receiving-project-selector"
          data-selected-id={accessibleSelectedProject?.id ?? ''}
          data-selected-label={accessibleSelectedProject?.item?.name ?? ''}
          key={`selectProject-${isProjectsLoading}-${accessibleSelectedProject?.id}`}
          placeholder="Select Project"
          items={allProjects ?? []}
          keyForDisplay="name"
          keyForValue="id"
          onSelect={handleSelectProject}
          getEntireItem={true}
          disabled={isProjectsLoading}
          createDescription={(item) => `Project ID : ${item.id}`}
          defaultValue={
            accessibleSelectedProject?.id
              ? [accessibleSelectedProject.id]
              : []
          }
          multiple={false}
          _icon={{
            color: "accent1.contrast",
          }}
          _placeholder={{ color: "accent1.contrast" }}
          color="accent1.contrast"
        />
      </FormLabel>
            {!isRfiSubmittals && (
        <>
          <Separator
            orientation={{ base: "horizontal", md: "vertical" }}
            height={{ base: undefined, md: 5 }}
          />

          <FormLabel
            label="Release"
            labelProps={{ minWidth: "fit-content", color: "accent1.contrast" }}
            infoTipColor="accent1.contrast"
          >
            <ComboBox
              data-testid="tutorial-release-selector"
              data-selected-id={accessibleSelectedRelease?.id ?? ''}
              data-selected-label={accessibleSelectedRelease?.item?.number ?? ''}
              key={`selectRelease-${accessibleSelectedProject?.id}-${accessibleSelectedRelease?.id}-${filteredReleases?.length ?? 0}-${isReleasesLoading}`}
              placeholder="Select Release"
              items={filteredReleases ?? []}
              keyForDisplay="number"
              keyForValue="id"
              onSelect={handleSelectRelease}
              getEntireItem={true}
              disabled={!accessibleSelectedProject?.id || isReleasesLoading}
              createDescription={(item) => `Release ID : ${item.id}`}
              defaultValue={
                accessibleSelectedRelease?.id
                  ? [accessibleSelectedRelease.id]
                  : []
              }
              multiple={false}
              _icon={{ color: "accent1.contrast" }}
              _placeholder={{ color: "accent1.contrast" }}
              color="accent1.contrast"
            />
          </FormLabel>
        </>
      )}

      <Separator
        orientation={{ base: "horizontal", md: "vertical" }}
        height={{ base: undefined, md: 5 }}
      />
      <FormLabel
        label="Project Sheet"
        labelProps={{ minWidth: "fit-content", color: "accent1.contrast" }}
        infoTipColor="accent1.contrast"
      >
        <ComboBox
          key={`selectProjectSheet-${accessibleSelectedProjectSheet?.id}-${isProjectSheetsLoading}`}
          placeholder="Select Project Sheet"
          items={filteredProjectSheets ?? []}
          keyForDisplay="name"
          keyForValue="id"
          onSelect={handleSelectProjectSheet}
          getEntireItem={true}
          disabled={
            !(
              accessibleSelectedProject?.id &&
              accessibleSelectedRelease?.id
            ) ||
            isProjectSheetsLoading
          }
          createDescription={(item) => `Project Sheet ID : ${item.id}`}
          defaultValue={
            accessibleSelectedProjectSheet?.id
              ? [accessibleSelectedProjectSheet.id]
              : []
          }
          multiple={false}
          _icon={{
            color: "accent1.contrast",
          }}
          _placeholder={{ color: "accent1.contrast" }}
          color="accent1.contrast"
        />
      </FormLabel>
    </Stack>
  );
};

export default ActionMenu;
